import { LoadingPage, useToast } from "@qubit/autoparts";
import { ReactNode, useEffect, useState } from "react";

import { Outlet, useLocation, Navigate, useNavigate } from "react-router-dom";

import { ClientConfigFailedDialog } from "~/components/settings/ClientConfigFailedDialog";
import useFlag from "~/config/flags";
import HandRaiseReminderModal from "~/features/login/HandRaiseReminderModal";
import useModeUpdate from "~/hooks/useModeUpdate";

import { useSendInitialPongOnce } from "~/hooks/useSendInitialPongOnce";
import {
  selectAuthHeaderReady,
  selectClientConfigReady,
  selectClientConfigError,
  selectFlagsReadyClientId
} from "~/redux/selectors/appSelectors";

import { selectIsUserLoggedIn } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  useLazyGetAndonWorkstationsQuery,
  useToggleHandRaiseMutation
} from "~/redux/warehouse/andon.hooks";
import { clientConfigApi } from "~/redux/warehouse/clientConfig";

import { clearClientConfigError, setSignalREnabled } from "./app.slice";
import { settingsPathAllowsNoFulfillmentCenter } from "./privateRoute.utils";
import { useAppDispatch, useAppSelector } from "./store";

type PrivateRouteProps = {
  children?: ReactNode;
};

export function PrivateRoute({ children }: PrivateRouteProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const normalizedPath = location.pathname.toLowerCase();
  const isSettingsPathAllowingNoFc =
    settingsPathAllowsNoFulfillmentCenter(normalizedPath);
  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const loginPath =
    useAppSelector((state) => state.app.siteLoginPath) || "/login";

  const isAuthHeaderReady = useAppSelector(selectAuthHeaderReady);
  const clientConfigReady = useAppSelector(selectClientConfigReady);
  const clientConfigError = useAppSelector(selectClientConfigError);
  const isFlagsReady = useAppSelector(selectFlagsReadyClientId);

  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const { enableSideNavSubMenus, improvedErrorHandlingConfig } = useFlag();
  const [showHandRaiseModal, setShowHandRaiseModal] = useState(false);
  const [initialHandRaiseCheckDone, setInitialHandRaiseCheckDone] =
    useState(false);

  const [toggleHandRaise] = useToggleHandRaiseMutation();
  const [triggerGetClientConfig] =
    clientConfigApi.useLazyGetClientConfigQuery();
  const { errorToast } = useToast();

  const [triggerGetAndonWorkstations] = useLazyGetAndonWorkstationsQuery();

  const siteWorkstationId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.id
  );
  const autostoreGridId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.autostoreGridId
  );

  useEffect(() => {
    if (!siteWorkstationId || !usersFulfillmentCenter) {
      setInitialHandRaiseCheckDone(true);
      return;
    }

    const handRaisedCheck = async () => {
      try {
        const andonResult = await triggerGetAndonWorkstations({
          workstationIds: [siteWorkstationId]
        });

        const isHandRaised =
          andonResult.data?.workstations.find(
            (workstation) => workstation.workstationId === siteWorkstationId
          )?.handRaised ?? false;

        setInitialHandRaiseCheckDone(true);

        if (isHandRaised) {
          setShowHandRaiseModal(true);
        }
      } catch (error) {
        console.error("Failed to load andon data:", error);
        setInitialHandRaiseCheckDone(true);
      }
    };

    void handRaisedCheck();
  }, [triggerGetAndonWorkstations, siteWorkstationId, usersFulfillmentCenter]);

  useModeUpdate({ pathname: location.pathname });
  useSendInitialPongOnce();

  // enable signalR if the user is logged in and has a fulfillment center
  const signalREnabled = useAppSelector((state) => state.app.signalREnabled);

  // If the user is logged in and has a fulfillment center, enable SignalR
  useEffect(() => {
    if (
      isUserLoggedIn &&
      usersFulfillmentCenter?.fulfillmentCenterId &&
      !signalREnabled
    ) {
      dispatch(setSignalREnabled(true));
    }
  }, [isUserLoggedIn, usersFulfillmentCenter, signalREnabled, dispatch]);

  if (!isUserLoggedIn) {
    return <Navigate to={loginPath} replace />;
  }

  if (clientConfigError) {
    if (!improvedErrorHandlingConfig) {
      return <Navigate to="/logout" replace />;
    }

    return (
      <>
        <LoadingPage loadingMessage="Loading..." />
        <ClientConfigFailedDialog
          open
          onRetry={() => {
            dispatch(clearClientConfigError());
            void triggerGetClientConfig();
          }}
          onGoToLogout={() => {
            navigate("/logout", { replace: true });
          }}
        />
      </>
    );
  }

  // Wait until configuration and flags are ready.
  if (!isAuthHeaderReady || !clientConfigReady || !isFlagsReady) {
    return <LoadingPage loadingMessage="Loading..." />;
  }

  // If the user doesn't have a fulfillment center and is not on settings, redirect.
  if (!usersFulfillmentCenter && !isSettingsPathAllowingNoFc) {
    return (
      <Navigate
        to={enableSideNavSubMenus ? "/settings/general" : "/settings"}
        replace
      />
    );
  }

  if (showHandRaiseModal) {
    return (
      <>
        <HandRaiseReminderModal
          variant="login"
          onLowerHand={async () => {
            if (siteWorkstationId && autostoreGridId) {
              try {
                await toggleHandRaise({
                  workstationId: siteWorkstationId,
                  autostoreGridId,
                  raiseHand: false
                });
              } catch {
                errorToast("Unable to lower hand");
              }
            }
            setShowHandRaiseModal(false);
          }}
          onKeepHandRaised={() => {
            setShowHandRaiseModal(false);
          }}
        />
      </>
    );
  }

  // Show loading until we've completed the initial hand raise check
  if (!initialHandRaiseCheckDone) {
    return <LoadingPage loadingMessage="Loading..." />;
  }

  return children ? <>{children}</> : <Outlet />;
}
